import { useMemo, useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  FileText,
  User,
  Calendar,
  Activity,
  Download,
  Share,
  UploadCloud,
  Pencil,
  Clock,
  AlertCircle,
  Receipt,
  Send,
  XCircle,
  Sparkles,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { ReceiptPreview } from "../components/ReceiptPreview";
import { format, parseISO } from "date-fns";
import { useReceipts } from "../../lib/receipts/useReceipts";
import { useExpenses } from "../../lib/expenses/useExpenses";
import { useAuth } from "../../lib/auth/useAuth";
import { useCapabilities } from "../../lib/permissions/useCapabilities";
import { ReceiptUploadButton } from "../components/ReceiptUploadButton";
import { NewExpenseDialog } from "../components/NewExpenseDialog";
import { formatAmountCents, formatIsoDate } from "../../lib/format";
import type { Expense, ExpenseStatus } from "../../lib/expenses/types";
import { generateNarrative } from "../../lib/expenses/narrative";

function StatusBadge({ status }: { status: ExpenseStatus }) {
  switch (status) {
    case "ready":
      return (<span data-testid="expense-detail-status" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 rounded-lg border border-green-100"><CheckCircle2 className="w-3.5 h-3.5" /> Ready</span>);
    case "needs_info":
      return (<span data-testid="expense-detail-status" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg border border-amber-100"><AlertCircle className="w-3.5 h-3.5" /> Needs info</span>);
    case "processing":
      return (<span data-testid="expense-detail-status" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg border border-blue-100"><Receipt className="w-3.5 h-3.5" /> Processing</span>);
    case "draft":
      return (<span data-testid="expense-detail-status" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-slate-50 text-slate-600 rounded-lg border border-slate-200"><Clock className="w-3.5 h-3.5" /> Draft</span>);
    case "pending":
      return (<span data-testid="expense-detail-status" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-50 text-amber-800 rounded-lg border border-amber-200"><Clock className="w-3.5 h-3.5" /> Pending review</span>);
    case "approved":
      return (<span data-testid="expense-detail-status" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-100"><CheckCircle2 className="w-3.5 h-3.5" /> Approved</span>);
    case "rejected":
      return (<span data-testid="expense-detail-status" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-red-50 text-red-700 rounded-lg border border-red-100"><XCircle className="w-3.5 h-3.5" /> Rejected</span>);
    default:
      return (<span data-testid="expense-detail-status" data-status={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-500 rounded-lg border border-slate-200">Archived</span>);
  }
}

function formatTimelineTimestamp(iso: string): string {
  try { return format(parseISO(iso), "MMM d, yyyy 'at' h:mm a"); } catch { return iso; }
}

function csvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '""';
  const str = String(value);
  return `"${str.replace(/"/g, '""')}"`;
}

function buildCsv(expense: Expense): string {
  const header = ["vendor","date","amount","category","tax_policy","deductible_amount","business_purpose","status"];
  const amount = (expense.amountCents / 100).toFixed(2);
  const deductible = expense.deductibleAmountCents === null ? "" : (expense.deductibleAmountCents / 100).toFixed(2);
  const row = [expense.vendor, expense.occurredOn, amount, expense.categoryLabel ?? "", expense.taxPolicyLabel ?? "", deductible, expense.businessPurpose ?? "", expense.status];
  return `${header.join(",")}\n${row.map(csvCell).join(",")}\n`;
}

function triggerCsvDownload(expense: Expense): void {
  const csv = buildCsv(expense);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safeVendor = expense.vendor.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48);
  a.download = `expense-${safeVendor || expense.id}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function ExpenseDetail() {
  const { id } = useParams<{ id: string }>();
  const { getById, submit, approve, reject, expenses } = useExpenses();
  const { getByExpense } = useReceipts();
  const { user } = useAuth();
  const { can } = useCapabilities();
  const navigate = useNavigate();

  const currentUserId = user?.id ?? null;
  // Phase 1: approve gates on the capability, not the role. Self-approval
  // is still allowed under the current defaults (admin/owner bundles both
  // include approve:expenses); phase 3 will split on submittedBy == caller.
  const canApproveExpenses = can("approve:expenses");

  const expense = id ? getById(id) : undefined;
  const linkedReceipt = expense ? getByExpense(expense.id) : undefined;

  const [editOpen, setEditOpen] = useState(false);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canSubmit =
    expense != null &&
    (expense.submittedBy == null || expense.submittedBy === currentUserId) &&
    ["draft", "needs_info", "rejected"].includes(expense.status);
  const canApprove = expense != null && canApproveExpenses && expense.status === "pending";
  const canReject = canApprove;

  const timelineEvents = useMemo(() => {
    if (!expense) return [];
    const events: Array<{ key: string; label: string; timestamp: string; tone: "blue" | "green" | "slate" | "amber" | "red"; }> = [];
    events.push({ key: "created", label: "Expense created", timestamp: expense.createdAt, tone: "slate" });
    if (linkedReceipt) {
      events.push({ key: "receipt", label: "Receipt attached", timestamp: linkedReceipt.uploadedAt, tone: "blue" });
    }
    if (expense.submittedAt) {
      events.push({ key: "submitted", label: "Submitted for review", timestamp: expense.submittedAt, tone: "blue" });
    }
    if (expense.approvedAt) {
      events.push({
        key: "approved",
        label: expense.status === "approved" ? "Approved" : expense.status === "rejected" ? "Rejected" : "Reviewed",
        timestamp: expense.approvedAt,
        tone: expense.status === "approved" ? "green" : "red",
      });
    }
    if (expense.updatedAt && expense.updatedAt !== expense.createdAt && expense.status === "ready") {
      events.push({ key: "status", label: "Marked ready", timestamp: expense.updatedAt, tone: "green" });
    }
    return events.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  }, [expense, linkedReceipt]);

  async function handleSubmit() {
    if (!expense || !canSubmit) return;
    setBusy(true);
    try {
      await submit(expense.id);
      navigate("/inbox");
    } finally {
      setBusy(false);
    }
  }

  async function handleApprove() {
    if (!expense || !canApprove) return;
    setBusy(true);
    try {
      await approve(expense.id);
      navigate("/inbox");
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    if (!expense || !canReject) return;
    const reason = typeof window !== "undefined"
      ? window.prompt("Reason for rejection?") ?? ""
      : "";
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await reject(expense.id, reason.trim());
      navigate("/inbox");
    } finally {
      setBusy(false);
    }
  }

  if (!expense) {
    return (
      <div className="h-full flex flex-col gap-6 max-w-6xl mx-auto">
        <div data-testid="expense-detail-not-found" className="flex-1 flex flex-col items-center justify-center gap-4 text-center p-8 text-slate-500">
          <AlertTriangle className="w-10 h-10 text-slate-300" />
          <div>
            <div className="text-lg font-semibold text-slate-900">Expense not found</div>
            <div className="text-sm text-slate-500 mt-1">The expense you're looking for no longer exists.</div>
          </div>
          <Link to="/expenses" className="px-4 py-2 text-sm font-medium text-white bg-[#0070F2] hover:bg-[#005BCA] rounded-md transition-colors">Back to Expenses</Link>
        </div>
      </div>
    );
  }

  const categoryLabel = expense.categoryLabel?.trim() || (expense.categoryId ? "Unknown" : "Uncategorized");
  const taxPolicyLabel = expense.taxPolicyLabel?.trim() || (expense.taxPolicyId ? "Unknown" : "No policy");
  const deductibleLabel = expense.deductibleAmountCents === null ? "—" : formatAmountCents(expense.deductibleAmountCents, expense.currency);
  const taxAmountLabel = formatAmountCents(expense.taxAmountCents, expense.currency);
  const confidenceDisplay = expense.confidenceScore !== null && expense.confidenceScore !== undefined ? String(expense.confidenceScore) : "—";

  const narrative = expense
    ? generateNarrative(expense, expenses, Boolean(linkedReceipt))
    : null;

  const editInitialValues = {
    vendor: expense.vendor,
    occurredOn: expense.occurredOn,
    amountCents: expense.amountCents,
    categoryId: expense.categoryId,
    categoryLabel: expense.categoryLabel ?? null,
    taxPolicyId: expense.taxPolicyId,
    taxPolicyLabel: expense.taxPolicyLabel ?? null,
    businessPurpose: expense.businessPurpose ?? undefined,
  };

  async function handleShare(): Promise<void> {
    if (typeof window === "undefined") return;
    const href = window.location.href;
    try {
      if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(href); }
      else {
        const ta = document.createElement("textarea");
        ta.value = href;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopyMessage("Link copied");
    } catch { setCopyMessage("Copy failed"); }
    setTimeout(() => setCopyMessage(null), 2000);
  }

  return (
    <div className="h-full flex flex-col gap-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Link to="/expenses" aria-label="Back to expenses" className="p-2 -ml-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl font-bold text-slate-900">Expense Detail</h1>
          <StatusBadge status={expense.status} />
        </div>
        <div className="flex gap-2 items-center relative flex-wrap">
          {copyMessage && (
            <div data-testid="expense-detail-copy-toast" className="absolute right-full mr-3 whitespace-nowrap text-xs font-medium bg-slate-900/90 text-white px-2.5 py-1 rounded-md shadow-sm">{copyMessage}</div>
          )}
          {canSubmit && (
            <button type="button" data-testid="expense-detail-submit" onClick={handleSubmit} disabled={busy}
              className="flex items-center gap-2 px-4 py-2 text-white bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 font-medium rounded-xl shadow-sm transition-colors text-sm">
              <Send className="w-4 h-4" /> Submit
            </button>
          )}
          {canApprove && (
            <button type="button" data-testid="expense-detail-approve" onClick={handleApprove} disabled={busy}
              className="flex items-center gap-2 px-4 py-2 text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 font-medium rounded-xl shadow-sm transition-colors text-sm">
              <CheckCircle2 className="w-4 h-4" /> Approve
            </button>
          )}
          {canReject && (
            <button type="button" data-testid="expense-detail-reject" onClick={handleReject} disabled={busy}
              className="flex items-center gap-2 px-4 py-2 text-white bg-red-600 hover:bg-red-700 disabled:bg-slate-300 font-medium rounded-xl shadow-sm transition-colors text-sm">
              <XCircle className="w-4 h-4" /> Reject
            </button>
          )}
          <button type="button" data-testid="expense-detail-edit" onClick={() => setEditOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 font-medium rounded-xl shadow-sm transition-colors text-sm">
            <Pencil className="w-4 h-4" /> Edit
          </button>
          <button type="button" data-testid="expense-detail-download" onClick={() => triggerCsvDownload(expense)}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 font-medium rounded-xl shadow-sm transition-colors text-sm">
            <Download className="w-4 h-4" /> Download
          </button>
          <button type="button" data-testid="expense-detail-share" onClick={() => { void handleShare(); }}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 font-medium rounded-xl shadow-sm transition-colors text-sm">
            <Share className="w-4 h-4" /> Share
          </button>
        </div>
      </div>

      <div className="flex flex-1 gap-6 overflow-hidden">
        <div className="w-2/3 flex flex-col gap-6 overflow-y-auto pr-2 pb-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 data-testid="expense-detail-vendor" className="text-xl font-bold text-slate-900">{expense.vendor}</h2>
                <p className="text-slate-500 mt-1">{formatIsoDate(expense.occurredOn, "MMMM d, yyyy")}</p>
              </div>
              <div className="text-right">
                <span data-testid="expense-detail-amount" className="text-2xl font-bold text-slate-900">{formatAmountCents(expense.amountCents, expense.currency)}</span>
                <p data-testid="expense-detail-category" className="text-slate-500 text-sm mt-1">{categoryLabel}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1 p-4 bg-slate-50 rounded-xl">
                <span className="text-xs text-slate-500 font-medium">Employee</span>
                <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                  <User className="w-4 h-4 text-slate-400" /> {expense.employee ?? "—"}
                </div>
              </div>
              <div className="flex flex-col gap-1 p-4 bg-slate-50 rounded-xl">
                <span className="text-xs text-slate-500 font-medium">Department</span>
                <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                  <FileText className="w-4 h-4 text-slate-400" /> {expense.department ?? "—"}
                </div>
              </div>

              <div className="flex flex-col gap-1 p-4 bg-slate-50 rounded-xl">
                <span className="text-xs text-slate-500 font-medium">Tax Amount</span>
                <div className="text-sm font-medium text-slate-900">{taxAmountLabel}</div>
              </div>
              <div className="flex flex-col gap-1 p-4 bg-slate-50 rounded-xl">
                <span className="text-xs text-slate-500 font-medium">Tax Policy (Deductibility)</span>
                <div className="flex items-center gap-2">
                  <span data-testid="expense-detail-tax-policy" className="text-sm font-medium text-slate-900 bg-blue-100 text-blue-800 px-2 py-0.5 rounded-md">{taxPolicyLabel}</span>
                  <span className="text-sm text-slate-500">({deductibleLabel})</span>
                </div>
              </div>

              <div className="col-span-2 flex flex-col gap-1 p-4 bg-slate-50 rounded-xl">
                <span className="text-xs text-slate-500 font-medium">Business Purpose</span>
                <p data-testid="expense-detail-purpose" className="text-sm font-medium text-slate-900 mt-1 whitespace-pre-wrap">
                  {expense.businessPurpose?.trim() || (<span className="text-slate-400 italic">Not provided</span>)}
                </p>
              </div>

              {/* Wave 11 approval panel */}
              <div className="col-span-2 flex flex-col gap-2 p-4 bg-slate-50 rounded-xl">
                <span className="text-xs text-slate-500 font-medium uppercase tracking-wide">Approval</span>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="text-xs text-slate-500">Submitted by</div>
                    <div data-testid="expense-detail-submitted-by" className="font-medium text-slate-900">{expense.submittedBy ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Submitted at</div>
                    <div data-testid="expense-detail-submitted-at" className="font-medium text-slate-900">{expense.submittedAt ? formatTimelineTimestamp(expense.submittedAt) : "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Approved by</div>
                    <div data-testid="expense-detail-approved-by" className="font-medium text-slate-900">{expense.approvedBy ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Approved at</div>
                    <div data-testid="expense-detail-approved-at" className="font-medium text-slate-900">{expense.approvedAt ? formatTimelineTimestamp(expense.approvedAt) : "—"}</div>
                  </div>
                  {expense.rejectionReason && (
                    <div className="col-span-2">
                      <div className="text-xs text-slate-500">Rejection reason</div>
                      <div data-testid="expense-detail-rejection-reason" className="font-medium text-red-700 whitespace-pre-wrap">{expense.rejectionReason}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-400" /> Attached Receipt
              </h3>
              <ReceiptUploadButton linkedExpenseId={expense.id} data-testid="expense-detail-attach-receipt" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 font-medium px-2 py-1 rounded-md hover:bg-slate-100 transition-colors">
                <UploadCloud className="w-4 h-4" /> {linkedReceipt ? "Replace" : "Attach receipt"}
              </ReceiptUploadButton>
            </div>
            <div className="aspect-[4/3] bg-slate-100 rounded-xl overflow-hidden flex items-center justify-center border border-slate-200 relative">
              {linkedReceipt ? (
                <ReceiptPreview testId="expense-receipt-preview" receipt={linkedReceipt} alt={`Receipt for ${expense.vendor}`} className="object-contain h-full w-full opacity-95" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-slate-400 px-4 py-8 text-center">
                  <UploadCloud className="w-8 h-8" />
                  <div className="text-sm font-medium">No receipt attached</div>
                  <div className="text-xs">Click "Attach receipt" to add one.</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="w-1/3 flex flex-col gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" /> Audit Assessment
            </h3>
            <div className="flex items-center gap-4 mb-6">
              <div data-testid="expense-detail-confidence" className={`w-16 h-16 rounded-full border-4 flex items-center justify-center ${
                expense.confidenceScore === null || expense.confidenceScore === undefined ? "border-slate-300" : expense.confidenceScore >= 80 ? "border-green-500" : "border-amber-500"
              }`}>
                <span className="text-lg font-bold text-slate-700">{confidenceDisplay}</span>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">
                  {expense.confidenceScore === null || expense.confidenceScore === undefined ? "No score yet" : expense.confidenceScore >= 80 ? "High confidence" : "Review recommended"}
                </p>
                <p className="text-xs text-slate-500">{linkedReceipt ? "Receipt attached" : "No receipt — attach one to raise confidence"}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className={`flex items-center justify-between p-3 rounded-xl border text-sm font-medium ${linkedReceipt ? "bg-green-50 text-green-700 border-green-100" : "bg-amber-50 text-amber-700 border-amber-100"}`}>
                <div className="flex items-center gap-2">
                  {linkedReceipt ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  Receipt {linkedReceipt ? "matched" : "missing"}
                </div>
              </div>
              <div className={`flex items-center justify-between p-3 rounded-xl border text-sm font-medium ${expense.taxPolicyId ? "bg-green-50 text-green-700 border-green-100" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                <div className="flex items-center gap-2">
                  {expense.taxPolicyId ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  Policy: {taxPolicyLabel}
                </div>
              </div>
              <div data-testid="expense-detail-project-code" className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200 text-sm text-slate-500">
                <div className="flex items-center gap-2 font-medium">
                  <Clock className="w-4 h-4" /> Project code
                </div>
                <span className="text-xs font-semibold uppercase tracking-wider bg-slate-200/70 px-2 py-0.5 rounded-full">Coming soon</span>
              </div>
            </div>
            {narrative && (
              <div className="mt-3 flex gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
                <Sparkles className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs leading-relaxed text-blue-800">{narrative}</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex-1">
            <h3 className="font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-400" /> Activity Timeline
            </h3>
            {timelineEvents.length === 0 ? (
              <p className="text-sm text-slate-500">No activity yet.</p>
            ) : (
              <div className="relative pl-6 space-y-6 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-px before:bg-slate-200">
                {timelineEvents.map((event) => (
                  <div key={event.key} className="relative">
                    <div className={`absolute -left-6 w-3 h-3 rounded-full ring-4 ring-white ${
                      event.tone === "green" ? "bg-green-500" : event.tone === "blue" ? "bg-blue-500" : event.tone === "amber" ? "bg-amber-500" : event.tone === "red" ? "bg-red-500" : "bg-slate-300"
                    }`} />
                    <p className="text-sm font-medium text-slate-900">{event.label}</p>
                    <p className="text-xs text-slate-500 mt-1">{formatTimelineTimestamp(event.timestamp)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <NewExpenseDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        mode="edit"
        expenseId={expense.id}
        initialValues={editInitialValues}
      />
    </div>
  );
}
